import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Loader2, Smartphone, WifiOff, CheckCircle2, X, RefreshCw, Copy } from 'lucide-react'
import { apiService } from '../../services/api'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../../services/errorUtils'

interface WhatsAppSetupProps {
  onComplete?: (data: { providerConnectionId: string; phoneNumberId: string; agentId?: string }) => void
  onCancel?: () => void
  trigger?: ReactNode
}

interface SetupState {
  phase: 'idle' | 'naming' | 'qr' | 'connected' | 'error'
  providerConnectionId?: string
  phoneNumberId?: string
  instanceName?: string
  agentId?: string
  label: string
  qrData?: { connectionState?: string; qrCode?: string }
  error?: string
  polling?: boolean
}

interface AgentOption {
  id: string
  agent_id?: string
  name?: string
}

const POLL_INTERVAL = 3000

export default function WhatsAppSetup({ onComplete, onCancel, trigger }: WhatsAppSetupProps) {
  const [open, setOpen] = useState(false)
  const [state, setState] = useState<SetupState>({ phase: 'naming', label: '' })
  const [submitting, setSubmitting] = useState(false)
  const [copied, setCopied] = useState(false)
  const [agents, setAgents] = useState<AgentOption[]>([])

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mountRef = useRef(true)

  useEffect(() => {
    mountRef.current = true
    return () => {
      mountRef.current = false
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [])

  const open_ = useCallback(() => {
    setOpen(true)
    setState({ phase: 'naming', label: '' })
  }, [])

  useEffect(() => {
    if (!open) return
    apiService.getAgents()
      .then((res) => setAgents(res.data?.data || []))
      .catch(() => setAgents([]))
  }, [open])

  const close = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    setOpen(false)
    setState({ phase: 'idle', label: '' })
    onCancel?.()
  }, [onCancel])

  const pollStatus = useCallback(async () => {
    if (!mountRef.current) return
    const pcId = state.providerConnectionId
    if (!pcId) return

    setState(s => ({ ...s, polling: true }))
    try {
      const res = await apiService.getWavoipStatus(pcId)
      const data = res.data?.data || res.data || {}

      if (data.is_connected) {
        if (data.elevenlabs_configured) {
          if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null }
          setState(s => ({ ...s, phase: 'connected', polling: false, qrData: { connectionState: 'connected' } }))
          onComplete?.({ providerConnectionId: pcId, phoneNumberId: state.phoneNumberId || '', agentId: state.agentId })
          toast.success('WhatsApp conectado com sucesso!')
          return
        }
      }

      if (!mountRef.current) return
      setState(s => ({
        ...s,
        polling: false,
        qrData: {
          connectionState: data.connection_state || 'waiting',
          qrCode: data.qr_code || undefined
        }
      }))
    } catch {
      if (mountRef.current) setState(s => ({ ...s, polling: false }))
    }
  }, [state.providerConnectionId, state.phoneNumberId, state.agentId, onComplete])

  const startPolling = useCallback(() => {
    if (pollingRef.current) return
    pollingRef.current = setInterval(pollStatus, POLL_INTERVAL)
    void pollStatus()
  }, [pollStatus])

  const createInstance = useCallback(async (label: string, agentId?: string) => {
    setSubmitting(true)
    try {
      const res = await apiService.createWavoipNumber({ label, assigned_agent: agentId })
      const data = res.data?.data || res.data || {}
      setState({
        phase: 'qr',
        providerConnectionId: data.provider_connection_id,
        phoneNumberId: data.phone_number_id,
        agentId: data.agent_id,
        instanceName: data.evolution_instance_name,
        label,
        qrData: { connectionState: 'waiting_scan' }
      })
    } catch (err) {
      const msg = getErrorMessage(err, 'Erro ao criar instância Evolution')
      setState({ phase: 'error', label, error: msg })
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }, [])

  const refreshQr = useCallback(async () => {
    if (!state.providerConnectionId) return
    setSubmitting(true)
    try {
      const res = await apiService.regenerateWavoipQr(state.providerConnectionId)
      const data = res.data?.data || {}
      setState(s => ({
        ...s,
        qrData: {
          ...s.qrData,
          qrCode: data.qr_code || s.qrData?.qrCode,
          connectionState: s.qrData?.connectionState || 'waiting_scan'
        }
      }))
      toast.success('QR code atualizado.')
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erro ao atualizar QR code'))
    } finally {
      setSubmitting(false)
    }
  }, [state.providerConnectionId])

  const disconnect = useCallback(async () => {
    if (!state.providerConnectionId) return
    setSubmitting(true)
    try {
      await apiService.disconnectWavoipNumber(state.providerConnectionId)
      setState(s => ({ ...s, phase: 'qr', qrData: { connectionState: 'disconnected' } }))
      toast.success('WhatsApp desconectado. Gere um novo QR para reconectar.')
      await refreshQr()
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erro ao desconectar WhatsApp'))
    } finally {
      setSubmitting(false)
    }
  }, [refreshQr, state.providerConnectionId])

  useEffect(() => {
    if (state.phase === 'qr' && state.providerConnectionId) {
      startPolling()
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [state.phase, state.providerConnectionId, startPolling])

  const copyInstance = useCallback(() => {
    if (state.instanceName) {
      navigator.clipboard.writeText(state.instanceName)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [state.instanceName])

  if (!open) {
    if (trigger) {
      return <div onClick={open_} className="cursor-pointer">{trigger}</div>
    }
    return (
      <button type="button" className="btn-primary" onClick={open_}>
        <Smartphone className="h-4 w-4" />
        Criar número WhatsApp
      </button>
    )
  }

  const qrCode = state.qrData?.qrCode
  const qrImageSrc = qrCode
    ? qrCode.startsWith('data:image')
      ? qrCode
      : `data:image/png;base64,${qrCode}`
    : null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm font-medium text-slate-500">
            {state.phase === 'naming' ? 'Criar número' : state.phase === 'qr' ? 'Conectar WhatsApp' : state.phase === 'connected' ? 'Conectado' : 'Erro'}
          </span>
          <button
            type="button"
            onClick={close}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {state.phase === 'naming' && (
          <form
            onSubmit={e => { e.preventDefault(); if (state.label.trim() && state.agentId) void createInstance(state.label, state.agentId) }}
            className="space-y-4"
          >
            <div className="text-center">
              <Smartphone className="mx-auto mb-2 h-8 w-8 text-primary" />
              <h3 className="text-lg font-semibold">Criar número WhatsApp</h3>
              <p className="mt-1 text-sm text-slate-500">
                Sistema cria instância Evolution + QR code automático.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Nome do número</label>
              <input
                className="input"
                placeholder="Ex: Comercial RJ"
                value={state.label}
                onChange={e => setState(s => ({ ...s, label: e.target.value }))}
                autoFocus
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Agente que vai usar este número</label>
              <select
                className="input"
                value={state.agentId || ''}
                onChange={e => setState(s => ({ ...s, agentId: e.target.value }))}
                required
              >
                <option value="">Selecione um agente</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.agent_id || agent.id}>
                    {agent.name || agent.agent_id || agent.id}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">Ao conectar, o número será criado/configurado no ElevenLabs e vinculado a este agente.</p>
            </div>
            <div className="flex gap-2">
              <button type="button" className="btn-secondary flex-1" onClick={close}>
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
                disabled={submitting || !state.label.trim() || !state.agentId}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Criar e gerar QR'}
              </button>
            </div>
          </form>
        )}

        {state.phase === 'qr' && (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-semibold">Escaneie com WhatsApp</h3>
              <p className="mt-1 text-sm text-slate-500">
                Conecte o WhatsApp à instância Evolution
              </p>
            </div>
            <div className="flex flex-col items-center gap-3 py-2">
              {qrImageSrc ? (
                <img
                  src={qrImageSrc}
                  alt="QR Code para conectar WhatsApp"
                  className="h-64 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-sm"
                />
              ) : state.polling ? (
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Smartphone className="h-8 w-8 text-slate-400" />
                  <p className="text-sm text-slate-500">Aguardando escaneamento...</p>
                  <p className="text-xs text-slate-400">
                    {state.qrData?.connectionState || 'waiting'}
                  </p>
                </div>
              )}
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">Instância Evolution</span>
                <button
                  type="button"
                  onClick={copyInstance}
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  {copied ? 'Copiado!' : <><Copy className="h-3 w-3" /> Copiar nome</>}
                </button>
              </div>
              <p className="break-all font-mono text-sm text-slate-700">
                {state.instanceName}
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
              <RefreshCw className={`h-3 w-3 ${state.polling ? 'animate-spin' : ''}`} />
              {state.polling ? 'Verificando conexão...' : 'Aguardando escaneamento'}
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              <button type="button" className="btn-secondary" disabled={submitting} onClick={() => void refreshQr()}>
                <RefreshCw className="h-4 w-4" />
                Regerar QR
              </button>
              <button type="button" className="btn-secondary" disabled={submitting} onClick={() => void disconnect()}>
                Desconectar
              </button>
              <button type="button" className="btn-secondary" onClick={close}>
                Fechar
              </button>
            </div>
          </div>
        )}

        {state.phase === 'connected' && (
          <div className="space-y-4 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
            <div>
              <h3 className="text-lg font-semibold text-green-700">WhatsApp conectado!</h3>
              <p className="mt-1 text-sm text-slate-500">
                Número criado e SIP trunk configurado.
              </p>
            </div>
            <button type="button" className="btn-primary w-full" onClick={close}>
              Concluir
            </button>
          </div>
        )}

        {state.phase === 'error' && (
          <div className="space-y-4 text-center">
            <WifiOff className="mx-auto h-10 w-10 text-red-400" />
            <div>
              <h3 className="font-semibold text-red-600">Erro na criação</h3>
              <p className="mt-1 text-sm text-slate-500">{state.error}</p>
            </div>
            <div className="flex gap-2">
              <button type="button" className="btn-secondary flex-1" onClick={close}>
                Fechar
              </button>
              <button
                type="button"
                className="btn-primary flex-1"
                onClick={() => setState({ phase: 'naming', label: '' })}
              >
                Tentar novamente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
